import React from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { adminNavItems } from "../config/navigation";
import { useAdminAuth } from "../auth/AdminAuthContext";

const drawerWidth = 272;

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = React.useState(false);
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    setLogoutConfirmOpen(false);
    logout();
    navigate("/admin/login");
  };

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);

  const sidebar = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
          display: "none"
        },
        background:
          "radial-gradient(circle at top, rgba(207, 170, 129, 0.16), transparent 34%), linear-gradient(180deg, #2f2118 0%, #1f1711 100%)",
        color: "#f8efe6"
      }}
    >
      <Box sx={{ px: 3, py: 3 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.28em", textTransform: "uppercase", color: "rgba(235, 217, 198, 0.7)" }}>
          Eventena
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, mt: 1, color: "#fff9f2" }}>
          Operations Console
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(232, 214, 196, 0.74)", mt: 1.1, maxWidth: 180 }}>
          Refined control for quotations, customers, events, and reporting.
        </Typography>
      </Box>

      <List
        sx={{
          px: 2,
          py: 1,
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none"
          }
        }}
      >
        {adminNavItems.map((item) => {
          const Icon = item.icon;
          const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
          return (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              sx={{
                mb: 0.75,
                borderRadius: 3,
                minHeight: 44,
                backgroundColor: active ? "rgba(201, 162, 120, 0.18)" : "transparent",
                color: active ? "#fff9f2" : "rgba(236, 220, 203, 0.82)",
                "&:hover": {
                  backgroundColor: "rgba(201, 162, 120, 0.12)"
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                <Icon />
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            p: 1.5,
            borderRadius: 3,
            backgroundColor: "rgba(201, 162, 120, 0.12)",
            border: "1px solid rgba(229, 207, 184, 0.08)"
          }}
        >
          <Avatar sx={{ bgcolor: "#b07d54", color: "#fff8f2" }}>
            {user?.displayName?.[0] || user?.firstName?.[0] || "A"}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 700 }}>
              {user?.displayName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim()}
            </Typography>
            <Typography noWrap variant="body2" sx={{ color: "rgba(232, 214, 196, 0.7)" }}>
              {user?.email}
            </Typography>
          </Box>
          <IconButton onClick={() => setLogoutConfirmOpen(true)} sx={{ color: "inherit" }}>
            <LogoutRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "linear-gradient(180deg, #f8f2ea 0%, #f1e4d6 100%)" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          displayPrint: "none",
          ml: { md: `${drawerWidth}px` },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: "rgba(255, 249, 242, 0.88)",
          backdropFilter: "blur(12px)",
          color: "text.primary",
          borderBottom: "1px solid rgba(111, 82, 58, 0.08)"
        }}
      >
        <Toolbar sx={{ minHeight: "68px !important" }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: "none" }, mr: 1 }}
          >
            <MenuRoundedIcon />
          </IconButton>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
              {adminNavItems.find((item) => location.pathname.startsWith(item.to))?.label || "Admin"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Elegant admin workspace for premium event operations.
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 }, displayPrint: "none" }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              overflow: "hidden",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" }
            }
          }}
        >
          {sidebar}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              borderRight: 0,
              overflow: "hidden",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" }
            }
          }}
        >
          {sidebar}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          overflowX: "hidden",
          p: { xs: 1.5, md: 2.5 },
          mt: "68px",
          "@media print": {
            mt: 0,
            p: 0,
            background: "white"
          }
        }}
      >
        <Outlet />
      </Box>

      <Dialog open={logoutConfirmOpen} onClose={() => setLogoutConfirmOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Logout?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary" }}>
            Are you sure you want to logout from the admin console?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleLogout}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
